<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .modal-content {
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(16px);
    padding: utils.in_rem(16px);

    .upload-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: utils.in_rem(8px);
      border: 2px dashed colors.get(interfaceSwGrey_10);
      border-radius: utils.in_rem(8px);
      padding: utils.in_rem(32px);
      transition: border-color 0.2s;
      cursor: pointer;

      &.drag-over {
        border-color: colors.get(primary_50);
        @include colors.background-color(primary_5);
      }

      .upload-text {
        @include mixins.body2();
        @include colors.color(textLighterBlack);
        text-align: center;
      }

      .upload-subtext {
        @include mixins.body3();
        @include colors.color(textLighterBlack);
      }

      input[type="file"] {
        display: none;
      }
    }

    .file-info {
      @include colors.background-color(interfaceSwGrey_5);
      display: flex;
      align-items: center;
      gap: utils.in_rem(12px);
      padding: utils.in_rem(12px);
      border-radius: utils.in_rem(6px);

      .file-details {
        flex: 1;

        .file-name {
          @include mixins.body2();
          @include colors.color(textBlack);
        }

        .file-size {
          @include mixins.body3();
          @include colors.color(textLighterBlack);
        }
      }
    }

    .error-message {
      @include colors.color(systemError_100);
      @include mixins.body3();
      text-align: center;
    }
  }

  .footer {
    display: flex;
    gap: utils.in_rem(8px);
    justify-content: flex-end;
    margin-top: utils.in_rem(16px);
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import FileUpload from "@sourcewhale/shared/svelte-design-system/components/molecules/FileUpload/FileUpload.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  import { showToast } from "@sourcewhale/core";
  import { saveCandidate } from "@sourcewhale/shared/src/svelte/api/candidates";
  import { saveFile } from "../../../../../api/files";
  import { fileToDataUrl } from "../../../../../utils/files.js";
  import { activePopUpModalStore, candidateStore } from "../../stores";

  const FILE_CONFIG = {
    allowedTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    maxFileSize: 64 * 1024 * 1024, // 64MB
    acceptedExtensions: [".pdf", ".doc", ".docx"],
  };

  let uploadState = $state({
    selectedFile: null,
    uploading: false,
    errorMessage: "",
  });

  const canUpload = $derived(
    uploadState.selectedFile &&
      !uploadState.uploading &&
      $candidateStore.candidateId
  );
  const modalTitle = $derived(
    $candidateStore.resumeFileId ? "Replace Resume" : "Upload Resume"
  );

  const handleFilesSelected = ({ files }) => {
    uploadState.selectedFile = files;
    uploadState.errorMessage = "";
  };

  const cancel = () => {
    activePopUpModalStore.set(null);
  };

  const upload = async () => {
    if (!canUpload) return;

    uploadState.uploading = true;
    uploadState.errorMessage = "";

    try {
      const dataUrl = await fileToDataUrl(uploadState.selectedFile);

      const fileResult = await saveFile(dataUrl, uploadState.selectedFile.name);

      await saveCandidate({
        candidateId: $candidateStore.candidateId,
        fileIds: [...($candidateStore.fileIds || []), fileResult.fileId],
        resumeFileId: fileResult.fileId,
      });

      showToast({
        title: "Success",
        subtitle: "Resume uploaded successfully",
        type: "success",
      });

      cancel();
    } catch {
      uploadState.errorMessage = "Failed to upload Resume";

      showToast({
        title: "Error",
        subtitle: "Failed to upload Resume",
        type: "danger",
      });
    } finally {
      uploadState.uploading = false;
    }
  };
</script>

<Modal size="small" onclose={cancel} blur={true} closeButton={false}>
  <div slot="header">
    <h2>{modalTitle}</h2>
  </div>
  <div class="modal-content">
    <FileUpload
      maxFiles={1}
      allowedTypes={FILE_CONFIG.allowedTypes}
      maxFileSize={FILE_CONFIG.maxFileSize}
      acceptedExtensions={FILE_CONFIG.acceptedExtensions}
      uploadText="Drop your Resume here or click to browse"
      uploadSubtext="Supports PDF, DOC, DOCX files up to 64MB"
      invalidTypeError="Please select a PDF or Word document"
      fileTooLargeError="File size must be less than 64MB"
      onFilesSelected={handleFilesSelected}
      isUploading={uploadState.uploading}
    />

    {#if uploadState.errorMessage}
      <div class="error-message" role="alert" aria-live="polite">
        {uploadState.errorMessage}
      </div>
    {/if}

    <div class="footer">
      <Button
        kind="ghost-blue"
        onclick={cancel}
        disabled={uploadState.uploading}
        class={uploadState.uploading ? "disabled" : ""}
      >
        Cancel
      </Button>
      <Button
        kind="primary"
        onclick={upload}
        disabled={!canUpload}
        class={canUpload ? "" : "disabled"}
      >
        Upload
      </Button>
    </div>
  </div>
</Modal>
